import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Grid,
  Container,
  Stack,
  Skeleton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Smartphone as PhoneIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Shield as ShieldIcon,
  Launch as LaunchIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAccount } from '../contexts/AccountContext';
import AvatarUpload from '../components/AvatarUpload';
import { Profile } from '../types/supabase';

interface UserAccount {
  account_id: string;
  account_name: string;
  role: string;
  plan_type: string;
  joined_at: string;
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userId, setUserId] = useState<string>('');

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  // Notifications state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);

  // User accounts state
  const [userAccounts, setUserAccounts] = useState<UserAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  useEffect(() => {
    loadUserData();
    loadUserAccounts();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || '');
        setUserId(user.id);

        // Load profile data from profiles table
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error loading profile:', profileError);
        } else if (profileData) {
          setProfile(profileData);
          setFirstName(profileData.first_name || '');
          setLastName(profileData.last_name || '');
          setPhone(profileData.phone || '');
          setAvatarUrl(profileData.avatar_url);
        } else {
          // Create profile if it doesn't exist
          const { error: createError } = await supabase
            .from('profiles')
            .insert([{
              id: user.id,
              first_name: user.user_metadata?.first_name || '',
              last_name: user.user_metadata?.last_name || '',
              role: 'user'
            }]);

          if (createError) {
            console.error('Error creating profile:', createError);
          } else {
            setFirstName(user.user_metadata?.first_name || '');
            setLastName(user.user_metadata?.last_name || '');
          }
        }
      }
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };

  const loadUserAccounts = async () => {
    setAccountsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load accounts the user belongs to
      const { data: accountData, error: accountError } = await supabase
        .from('account_users')
        .select(`
          account_id,
          role,
          joined_at,
          accounts!inner (
            id,
            name,
            plan_type
          )
        `)
        .eq('user_id', user.id);

      if (accountError) {
        console.error('Error loading user accounts:', accountError);
        setError(`Failed to load your accounts: ${accountError.message}`);
      } else if (accountData) {
        const formattedAccounts: UserAccount[] = accountData.map((item: any) => ({
          account_id: item.account_id,
          account_name: item.accounts.name,
          role: item.role,
          plan_type: item.accounts.plan_type,
          joined_at: item.joined_at,
        }));
        setUserAccounts(formattedAccounts);
      }
    } catch (err) {
      console.error('Error loading user accounts:', err);
      setError('Failed to load your accounts');
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    setProfileLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not found');
        return;
      }

      // Update profile in profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          first_name: firstName,
          last_name: lastName,
          phone: phone,
        });

      if (profileError) {
        setError(profileError.message);
      } else {
        // Also update auth metadata for consistency
        await supabase.auth.updateUser({
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`,
          }
        });

        setSuccess('Profile updated successfully');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess('Password updated successfully');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToAccount = (accountId: string) => {
    // Navigate to the account page or account settings
    navigate(`/account/${accountId}`);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'primary';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <BusinessIcon />;
      case 'admin':
        return <AdminIcon />;
      default:
        return <GroupIcon />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
      {/* Impersonation Warning */}
      {isImpersonating && impersonatedUser && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ⚠️ IMPERSONATION MODE: Personal Settings
          </Typography>
          <Typography variant="body2">
            You are viewing {impersonatedUser.email}'s personal settings.
            Changes made here will affect their profile, avatar, and personal preferences.
            Use caution when modifying personal information.
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          User Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your personal profile, security, and preferences
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack spacing={4}>
        {/* Profile Information Section */}
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Profile Information
              </Typography>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '200px 1fr' },
              gap: 4,
              alignItems: 'start'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                {userId && (
                  <AvatarUpload
                    currentAvatarUrl={avatarUrl}
                    onAvatarUpdate={setAvatarUrl}
                    userId={userId}
                    size={120}
                    userName={`${firstName} ${lastName}`.trim()}
                    userEmail={userEmail}
                  />
                )}
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3
              }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  variant="outlined"
                />
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={userEmail}
                    disabled
                    variant="outlined"
                    helperText="Email cannot be changed directly. Contact support if needed."
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  variant="outlined"
                  placeholder="+1 (555) 123-4567"
                />
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleProfileUpdate}
                      disabled={profileLoading}
                      startIcon={<SaveIcon />}
                    >
                      {profileLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={loadUserData}
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Security Settings Section */}
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Security Settings
              </Typography>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 4
            }}>
              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Change Password
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPasswords ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPasswords ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    size="small"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={showPasswords}
                        onChange={(e) => setShowPasswords(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show passwords"
                  />

                  <Button
                    variant="contained"
                    onClick={handlePasswordChange}
                    disabled={loading || !oldPassword || !newPassword || !confirmPassword}
                    startIcon={<ShieldIcon />}
                    size="small"
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </Button>
                </Stack>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Two-Factor Authentication
                </Typography>

                <Stack spacing={2}>
                  <Chip
                    label="Not Enabled"
                    color="warning"
                    icon={<PhoneIcon />}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Add an extra layer of security to your account with two-factor authentication.
                  </Typography>
                  <Button variant="outlined" disabled size="small">
                    Enable 2FA (Coming Soon)
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </CardContent>
        </Card>

        {/* Notification Preferences Section */}
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notification Preferences
              </Typography>
            </Box>

            <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive emails about important account activity"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Push Notifications"
                  secondary="Receive browser notifications for real-time updates"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={pushNotifications}
                    onChange={(e) => setPushNotifications(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Marketing Emails"
                  secondary="Receive emails about product updates and tips"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={marketingEmails}
                    onChange={(e) => setMarketingEmails(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* My Accounts Section */}
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                My Accounts
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Accounts you belong to and your role in each. Click on an account to manage its settings.
            </Typography>

            {accountsLoading ? (
              <Stack spacing={2}>
                {[1, 2].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            ) : userAccounts.length > 0 ? (
              <Stack spacing={2}>
                {userAccounts.map((account) => (
                  <Paper
                    key={account.account_id}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: currentAccount?.account_id === account.account_id ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-1px)',
                        boxShadow: (theme) => theme.shadows[4],
                      }
                    }}
                    onClick={() => handleNavigateToAccount(account.account_id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                          {account.account_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {account.account_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              icon={getRoleIcon(account.role)}
                              label={account.role}
                              size="small"
                              color={getRoleColor(account.role)}
                              sx={{ textTransform: 'capitalize' }}
                            />
                            <Chip
                              label={account.plan_type}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {currentAccount?.account_id === account.account_id && (
                          <Chip label="Current" color="success" size="small" />
                        )}
                        <LaunchIcon color="action" />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Accounts Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You don't belong to any accounts yet. Contact your administrator to get access.
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default SettingsPage;
